// @flow
import { fromUnixTime, isSameDay } from 'date-fns';
import api from '../api/catalog';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getCatalog } from './catalog';
import { getDeployment } from './config';
import { getLiveCatalogCardDatas } from './cardData';
import type { GlobalState } from '../rootReducer';
import { LOGIN_SUCCESS } from './login';
import ms from 'ms';
import { shouldShowLive } from '../../utils/live';

export const LOAD_UPCOMING_CATALOGS_FAIL = 'la/domain/upcomingCatalogs/LOAD_FAIL';
export const LOAD_UPCOMING_CATALOGS_REQUEST = 'la/domain/upcomingCatalogs/LOAD_REQUEST';
export const LOAD_UPCOMING_CATALOGS_SUCCESS = 'la/domain/upcomingCatalogs/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

const DEFAULT_STATE = {
    ids: [],
    loaded: 0,
    loading: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_UPCOMING_CATALOGS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_UPCOMING_CATALOGS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_UPCOMING_CATALOGS_SUCCESS:
            return {
                ...state,
                ids: action.payload.catalogs.map(catalog => catalog.catalogId).sort(),
                loaded: action.meta.actionTime,
                loading: false,
            };
        case LOGIN_SUCCESS:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.upcomingCatalogs;
const globalStateSelector = (state: GlobalState) => state;

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getUpcomingCatalogIds = createSelector(stateSelector, state => state.ids);

export const getLiveCatalogsIds = createSelector(
    [getUpcomingCatalogIds, globalStateSelector],
    (allUpcomingCatalogIds, state) => {
        return allUpcomingCatalogIds.filter(id => {
            const catalog = getCatalog(state, id);
            return catalog.catalogStatus === 'live';
        });
    },
);

export const getTrendingCatalogs = (state: GlobalState) => {
    const allUpcomingCatalogIds = getUpcomingCatalogIds(state);
    const liveCatalogCards = getLiveCatalogCardDatas(state, allUpcomingCatalogIds);
    const live = liveCatalogCards
        .filter(({ catalog }) => {
            const { catalogStatus, isCatalogOnly, saleStartTs } = catalog;
            return shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
        })
        .sort((a, b) => {
            const firstBidders = a.bidders + a.catalog.absenteeBidders;
            const secondBidders = b.bidders + b.catalog.absenteeBidders;
            if (firstBidders > secondBidders) return -1;
            if (firstBidders < secondBidders) return 1;
            return 0;
        })
        .slice(0, 3);

    return (
        live.reduce((trending, catalogCardData) => {
            if (catalogCardData.catalog.absenteeBidders + catalogCardData.bidders > 50) {
                return [...trending, catalogCardData.catalog.catalogId];
            }
            return trending;
        }, []) || []
    );
};

export const getTodaysCatalogIds = (state: GlobalState) => {
    const allUpcomingCatalogIds = getUpcomingCatalogIds(state);
    const today = new Date();
    const liveCatalogCards = getLiveCatalogCardDatas(state, allUpcomingCatalogIds);
    const allsorted = liveCatalogCards
        .filter(({ catalog }) => {
            const saleStart = fromUnixTime(catalog.saleStartTs);
            return isSameDay(today, saleStart);
        })
        .sort((a, b) => {
            const firstStart = a.catalog.saleStartTs;
            const secondStart = b.catalog.saleStartTs;
            if (firstStart < secondStart) return -1;
            if (firstStart > secondStart) return 1;
            return 0;
        });

    const live = allsorted
        .filter(({ catalog }) => {
            const { catalogStatus, isCatalogOnly, saleStartTs } = catalog;
            return shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
        })
        .sort((a, b) => {
            const firstBidders = a.bidders + a.catalog.absenteeBidders;
            const secondBidders = b.bidders + b.catalog.absenteeBidders;
            if (firstBidders > secondBidders) return -1;
            if (firstBidders < secondBidders) return 1;
            return 0;
        })
        .map(({ catalog }) => catalog.catalogId);

    const rest = allsorted
        .filter(({ catalog }) => {
            const { catalogStatus, isCatalogOnly, saleStartTs } = catalog;
            return !shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
        })
        .map(({ catalog }) => catalog.catalogId);

    return [...live, ...rest];
};

const shouldFetchUpcomingCatalogs = createSelector(
    [getUpcomingCatalogIds, loadedSelector, loadingSelector],
    (items, loaded, loading) => {
        if (items.length) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) return false;
        }
        return !loading;
    },
);

/* ACTION CREATORS */
const loadUpcomingCatalogs = (pageSize, today, forceUpdate) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: LOAD_UPCOMING_CATALOGS_REQUEST,
        });
        const response = await api.fetchUpcomingCatalogs({
            authToken,
            deployment,
            forceUpdate,
            pageSize,
            today,
        });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_UPCOMING_CATALOGS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_UPCOMING_CATALOGS_FAIL,
        });
    }
};

export const fetchUpcomingCatalogsIfNeeded = (pageSize: number, today: any, forceUpdate: boolean = false) => async (
    dispatch: Function,
    getState: Function,
) => {
    if (forceUpdate || shouldFetchUpcomingCatalogs(getState())) {
        return dispatch(loadUpcomingCatalogs(pageSize, today, forceUpdate));
    }
    return Promise.resolve();
};
